import {MilestoneIcon} from '@primer/octicons-react'
import {Link, Octicon, Tooltip} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {DemilestonedEvent$key} from './__generated__/DemilestonedEvent.graphql'
import {TimelineRow} from './row/TimelineRow'

type DemilestonedEventProps = {
  queryRef: DemilestonedEvent$key
  issueUrl: string
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function DemilestonedEvent({
  queryRef,
  issueUrl,
  highlightedEventId,
  refAttribute,
}: DemilestonedEventProps): JSX.Element {
  const {actor, createdAt, milestoneTitle, milestone, databaseId} = useFragment(
    graphql`
      fragment DemilestonedEvent on DemilestonedEvent {
        databaseId
        createdAt
        actor {
          ...EventActor
          ...EventActorAvatarItemBadge
        }
        milestoneTitle
        milestone {
          url
        }
      }
    `,
    queryRef,
  )

  const highlighted = String(databaseId) === highlightedEventId

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      actorAvatar={actor}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
    >
      <>
        {LABELS.timeline.removedFromMilestone}
        {getWrappedMilestoneLink(milestone?.url, milestoneTitle)}
        {LABELS.timeline.milestone}{' '}
      </>
    </TimelineRow>
  )
}

export function getWrappedMilestoneLink(url: string | undefined, title: string): JSX.Element {
  if (url !== undefined) {
    return getMilestoneLink(url, title)
  }
  return <Tooltip aria-label={LABELS.timeline.milestoneDeleted}>{getMilestoneLink(url, title)}</Tooltip>
}

function getMilestoneLink(url: string | undefined, title: string): JSX.Element {
  return (
    <>
      {' '}
      <Octicon icon={MilestoneIcon} />{' '}
      <Link href={url} sx={{color: 'fg.default'}} muted>
        {title}
      </Link>{' '}
    </>
  )
}

try{ DemilestonedEvent.displayName ||= 'DemilestonedEvent' } catch {}